import React, { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
    Container, Typography, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Button, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Box, Grid, Switch, Avatar,
    useTheme, useMediaQuery, Card, CardContent, Divider, Chip,
    FormControlLabel
} from '@mui/material';
// import { Link } from 'react-router-dom';
import { Edit, Delete, Add, CloudUpload, Close, ListAlt, Layers, ViewCompact } from '@mui/icons-material';
import welfareService from '../../utils/welfareService';
import { snackbar, successMessage } from '../../utils/showMessage';
import api from '../../utils/api';
const textFieldStyle = {
    mb: 2,
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: '#fff',
        '& fieldset': {
            borderColor: 'rgba(0, 0, 0, 0.1)',
        },
    }
};

const WelfareCategories = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const [categories, setCategories] = useState([]);
    const [open, setOpen] = useState(false);
    const [currentCategory, setCurrentCategory] = useState(null);
    // const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [formData, setFormData] = useState({ title: '', description: '', order: 0, image: '', showOnHomePage: false, subCategories: [] });
    const [subCategory, setSubCategory] = useState({ title: '', description: '', order: 0, image: '', isActive: true, showOnHomePage: false });
    const [editingSubIndex, setEditingSubIndex] = useState(-1);

    useEffect(() => { fetchCategories(); }, []);

    const fetchCategories = async () => {
        // setLoading(true);
        try {
            const data = await welfareService.getCategories(true);
            setCategories(data || []);
        } catch (err) {
            console.error(err);
            snackbar('خطا در بارگذاری اطلاعات');
        } finally { /* setLoading(false); */ }
    };

    const handleOpen = (category = null) => {
        if (category) {
            setCurrentCategory(category);
            setFormData({
                title: category.title || '',
                description: category.description || '',
                order: category.order || 0,
                image: category.image || '',
                showOnHomePage: category.showOnHomePage || false,
                subCategories: (category.subCategories || []).map(sub => ({ ...sub, showOnHomePage: sub.showOnHomePage || false })),
            });
        } else {
            setCurrentCategory(null);
            setFormData({ title: '', description: '', order: 0, image: '', showOnHomePage: false, subCategories: [] });
        }
        setSubCategory({ title: '', description: '', order: 0, image: '', isActive: true, showOnHomePage: false });
        setEditingSubIndex(-1);
        setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubChange = (e) => {
        const { name, value, type, checked } = e.target;
        setSubCategory(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubImageUpload = async (file) => {
        if (!file) return;
        const payload = new FormData();
        payload.append('file', file);
        try {
            const res = await api.post('/welfare/upload-image', payload);
            setSubCategory(prev => ({ ...prev, image: res.data.filename }));
            successMessage('تصویر زیر دسته با موفقیت بارگذاری شد');
        } catch (err) {
            console.error(err);
            snackbar('خطا در بارگذاری تصویر زیر دسته');
        }
    };

    const handleAddOrEditSubCategory = () => {
        if (!subCategory.title || subCategory.title.trim() === '') {
            snackbar('عنوان زیر دسته اجباری است');
            return;
        }
        if (subCategory.id) {
            // ویرایش بر اساس id
            setFormData(prev => ({
                ...prev,
                subCategories: prev.subCategories.map(sc => sc.id === subCategory.id ? subCategory : sc)
            }));
        } else {
            // افزودن
            const newSub = { ...subCategory, id: uuidv4() };
            setFormData(prev => ({ ...prev, subCategories: [...prev.subCategories, newSub] }));
        }
        setSubCategory({ title: '', description: '', order: 0, image: '', isActive: true, showOnHomePage: false });
        setEditingSubIndex(-1);
    };

    const handleEditSubCategory = (id) => {
        const found = formData.subCategories.find(sc => sc.id === id);
        if (found) setSubCategory(found);
        setEditingSubIndex(-1);
    };

    const handleDeleteSubCategory = (id) => {
        setFormData(prev => ({ ...prev, subCategories: prev.subCategories.filter(sc => sc.id !== id) }));
        setSubCategory({ title: '', description: '', order: 0, image: '', isActive: true });
        setEditingSubIndex(-1);
    };

    const handleImageUpload = async (file) => {
        if (!file) return;
        const payload = new FormData();
        payload.append('file', file);
        try {
            const res = await api.post('/welfare/upload-image', payload);
            setFormData(prev => ({ ...prev, image: res.data.filename }));
            successMessage('تصویر با موفقیت بارگذاری شد');
        } catch (err) {
            console.error(err);
            snackbar('خطا در بارگذاری تصویر');
        }
    };

    const getImageUrl = (filename) => {
        if (!filename) return '';
        if (typeof filename === 'string' && filename.startsWith('http')) return filename;
        return `${process.env.REACT_APP_FILE_BASE_URL}/uploads/documents/${filename}`;
    };

    const handleSubmit = async () => {
        // Prevent submission if title is empty (category not selected/entered)
        if (!formData.title || formData.title.trim() === '') {
            snackbar('انتخاب یا وارد کردن عنوان دسته‌بندی اجباری است');
            return;
        }
        setSaving(true);
        try {
            if (currentCategory) {
                await welfareService.updateCategory(currentCategory._id, formData);
                successMessage('با موفقیت بروزرسانی شد');
            } else {
                await welfareService.createCategory(formData);
                successMessage('با موفقیت ایجاد شد');
            }
            await fetchCategories();
            handleClose();
        } catch (err) {
            console.error(err);
            snackbar('خطا در ذخیره اطلاعات');
        } finally { setSaving(false); }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('آیا از حذف این مورد اطمینان دارید؟')) return;
        try {
            await welfareService.deleteCategory(id);
            successMessage('با موفقیت حذف شد');
            fetchCategories();
        } catch (err) { console.error(err); snackbar('خطا در حذف'); }
    };

    const toggleActive = async (category) => {
        try {
            await welfareService.updateCategory(category._id, { isActive: !category.isActive });
            fetchCategories();
            successMessage('وضعیت با موفقیت تغییر کرد');
        } catch (err) { console.error(err); snackbar('خطا در بروزرسانی وضعیت'); }
    };

    return (
        <Container maxWidth="lg" sx={{ mt: isMobile ? 2 : 5, mb: 5, pt: isMobile ? 8 : 3 }}>
            <Box
                display="flex"
                flexDirection={isMobile ? 'column' : 'row'}
                justifyContent="space-between"
                alignItems={isMobile ? 'stretch' : 'center'}
                gap={2}
                mb={3}
            >
                <Typography variant={isMobile ? "h6" : "h5"} fontWeight="bold" color="primary">
                    مدیریت دسته‌بندی رفاهی
                </Typography>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => handleOpen()}
                    fullWidth={isMobile}
                    sx={{ borderRadius: 3, px: 3 }}
                >
                    دسته‌بندی جدید
                </Button>
            </Box>

            {isMobile ? (
                <Grid container spacing={2}>
                    {categories.map((category) => (
                        <Grid item xs={12} key={category._id}>
                            <Card sx={{ borderRadius: 4, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
                                <CardContent sx={{ p: 2 }}>
                                    <Box display="flex" gap={2} alignItems="center">
                                        <Avatar
                                            src={getImageUrl(category.image)}
                                            variant="rounded"
                                            sx={{ width: 70, height: 70, borderRadius: 2, boxShadow: 1 }}
                                        />
                                        <Box flex={1}>
                                            <Typography variant="subtitle1" fontWeight="bold">
                                                {category.title}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
                                                {category.description || 'بدون توضیحات'}
                                            </Typography>
                                            <Box display="flex" gap={1} mt={1} alignItems="center">
                                                <Chip
                                                    label={`ترتیب: ${category.order ?? 0}`}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ borderRadius: 1 }}
                                                />
                                                <Box display="flex" alignItems="center" gap={0.5}>
                                                    <Typography variant="caption" color="text.secondary">وضعیت:</Typography>
                                                    <Switch
                                                        size="small"
                                                        checked={!!category.isActive}
                                                        onChange={() => toggleActive(category)}
                                                    />
                                                </Box>
                                            </Box>
                                        </Box>
                                    </Box>

                                    <Divider sx={{ my: 1.5, borderStyle: 'dashed' }} />

                                    <Box display="flex" justifyContent="flex-end" gap={1}>
                                        <IconButton
                                            size="small"
                                            sx={{ bgcolor: 'primary.light', color: 'primary.contrastText', '&:hover': { bgcolor: 'primary.main' } }}
                                            onClick={() => handleOpen(category)}
                                        >
                                            <Edit fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                            size="small"
                                            sx={{ bgcolor: 'error.light', color: 'error.contrastText', '&:hover': { bgcolor: 'error.main' } }}
                                            onClick={() => handleDelete(category._id)}
                                        >
                                            <Delete fontSize="small" />
                                        </IconButton>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>
            ) : (
                <TableContainer component={Paper} sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
                    <Table>
                        <TableHead sx={{ bgcolor: 'grey.50' }}>
                            <TableRow>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>تصویر</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>عنوان</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>توضیحات</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>ترتیب</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>وضعیت</TableCell>
                                <TableCell align="center" sx={{ fontWeight: 'bold' }}>عملیات</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {categories.map((category) => (
                                <TableRow key={category._id} hover>
                                    <TableCell align="right">
                                        <Avatar src={getImageUrl(category.image)} variant="rounded" sx={{ width: 60, height: 60, borderRadius: 1.5, boxShadow: 1 }} />
                                    </TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 500 }}>{category.title}</TableCell>
                                    <TableCell align="right" color="text.secondary">{category.description}</TableCell>
                                    <TableCell align="right">{category.order ?? 0}</TableCell>
                                    <TableCell align="center">
                                        <Switch checked={!!category.isActive} onChange={() => toggleActive(category)} color="primary" />
                                    </TableCell>
                                    <TableCell align="center">
                                        <Box display="flex" justifyContent="center" gap={0.5}>
                                            <IconButton color="primary" onClick={() => handleOpen(category)}>
                                                <Edit fontSize="small" />
                                            </IconButton>
                                            <IconButton color="error" onClick={() => handleDelete(category._id)}>
                                                <Delete fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
                PaperProps={{
                    sx: { borderRadius: isMobile ? 0 : 3 }
                }}
            >
                <DialogTitle sx={{
                    bgcolor: isMobile ? 'primary.main' : 'transparent',
                    color: isMobile ? 'white' : 'text.primary',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    p: 2
                }}>
                    <Typography variant="h6" fontWeight="bold">
                        {currentCategory ? 'ویرایش دسته‌بندی' : 'افزودن دسته‌بندی جدید'}
                    </Typography>
                    {isMobile && (
                        <IconButton color="inherit" onClick={handleClose} size="small">
                            <Close />
                        </IconButton>
                    )}
                </DialogTitle>
                <DialogContent dividers={isMobile}>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12} sm={4}>
                            <Box
                                display="flex"
                                flexDirection="column"
                                alignItems="center"
                                justifyContent="center"
                                height="100%"
                                border="2px dashed"
                                borderColor="divider"
                                borderRadius={3}
                                p={2}
                                sx={{ bgcolor: 'grey.50' }}
                            >
                                {formData.image ? (
                                    <img src={getImageUrl(formData.image)} alt="Category" style={{ maxWidth: '100%', maxHeight: 200, borderRadius: 8, marginBottom: 10, boxShadow: '0 4px 10px rgba(0,0,0,0.1)' }} />
                                ) : (
                                    <Typography variant="body2" color="textSecondary" mb={1}>تصویر دسته‌بندی</Typography>
                                )}
                                <Button variant="outlined" component="label" startIcon={<CloudUpload />} size="small" sx={{ borderRadius: 2 }}>
                                    {formData.image ? 'تغییر تصویر' : 'بارگذاری تصویر'}
                                    <input type="file" hidden accept="image/*" onChange={(e) => handleImageUpload(e.target.files[0])} />
                                </Button>
                            </Box>
                        </Grid>
                        <Grid item xs={12} sm={8}>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <TextField fullWidth label="عنوان دسته‌بندی" name="title" value={formData.title} onChange={handleChange} sx={textFieldStyle} />
                                </Grid>
                                <Grid item xs={12}>
                                    <TextField fullWidth label="توضیحات" name="description" value={formData.description} onChange={handleChange} multiline rows={isMobile ? 3 : 5} sx={textFieldStyle} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField fullWidth label="ترتیب نمایش" name="order" type="number" value={formData.order} onChange={handleChange} sx={textFieldStyle} />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <Box sx={{ ...textFieldStyle, display: 'flex', alignItems: 'center', height: '56px', px: 2, border: '1px solid rgba(0,0,0,0.1)', borderRadius: '12px' }}>
                                        <FormControlLabel
                                            control={<Switch checked={formData.showOnHomePage} onChange={handleChange} name="showOnHomePage" color="primary" />}
                                            label="نمایش در صفحه اصلی"
                                        />
                                    </Box>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>

                    {/* SubCategories Section */}
                    <Box mt={4}>
                        <Divider sx={{ mb: 3 }}>
                            <Chip label="مدیریت زیر دسته‌بندی‌ها" icon={<ListAlt />} variant="outlined" color="primary" />
                        </Divider>

                        <Paper sx={{ p: 2, borderRadius: 3, bgcolor: 'grey.50', mb: 3 }}>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom color="text.secondary">
                                {editingSubIndex > -1 ? 'ویرایش زیر دسته' : 'افزودن زیر دسته جدید'}
                            </Typography>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} sm={4}>
                                    <TextField fullWidth label="عنوان زیر دسته" name="title" value={subCategory.title} onChange={handleSubChange} sx={textFieldStyle} size="small" />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField fullWidth label="توضیحات" name="description" value={subCategory.description} onChange={handleSubChange} sx={textFieldStyle} size="small" />
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <TextField fullWidth label="ترتیب" name="order" type="number" value={subCategory.order} onChange={handleSubChange} sx={textFieldStyle} size="small" />
                                </Grid>
                                <Grid item xs={12} sm={2}>
                                    <Box display="flex" alignItems="center" gap={1}>
                                        <Button variant="outlined" component="label" startIcon={<CloudUpload />} size="small" sx={{ borderRadius: 2, height: '40px' }} fullWidth>
                                            {subCategory.image ? 'تغییر' : 'تصویر'}
                                            <input type="file" hidden accept="image/*" onChange={(e) => handleSubImageUpload(e.target.files[0])} />
                                        </Button>
                                        {subCategory.image && <Avatar src={getImageUrl(subCategory.image)} variant="rounded" sx={{ width: 40, height: 40 }} />}
                                    </Box>
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControlLabel
                                        control={<Switch checked={subCategory.showOnHomePage} onChange={handleSubChange} name="showOnHomePage" color="primary" />}
                                        label="نمایش در صفحه اصلی"
                                    />
                                </Grid>
                                <Grid item xs={12} sm={8} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                                    <Button
                                        variant="contained"
                                        color={editingSubIndex > -1 ? 'warning' : 'primary'}
                                        onClick={handleAddOrEditSubCategory}
                                        sx={{ borderRadius: 2, px: 4 }}
                                        startIcon={editingSubIndex > -1 ? <Edit /> : <Add />}
                                    >
                                        {editingSubIndex > -1 ? 'بروزرسانی زیر دسته' : 'افزودن زیر دسته'}
                                    </Button>
                                </Grid>
                            </Grid>
                        </Paper>

                        {formData.subCategories && formData.subCategories.length > 0 ? (
                            isMobile ? (
                                <Grid container spacing={2}>
                                    {formData.subCategories.map((sc, index) => (
                                        <Grid item xs={12} key={sc.id || index}>
                                            <Card variant="outlined" sx={{ borderRadius: 3 }}>
                                                <CardContent sx={{ p: 2 }}>
                                                    <Box display="flex" gap={2} alignItems="center">
                                                        <Avatar src={getImageUrl(sc.image)} variant="rounded" />
                                                        <Box flex={1}>
                                                            <Typography variant="body1" fontWeight="bold">{sc.title}</Typography>
                                                            <Typography variant="caption" color="text.secondary">{sc.description}</Typography>
                                                        </Box>
                                                        <Box>
                                                            <IconButton size="small" onClick={() => handleEditSubCategory(sc.id)} color="primary"><Edit fontSize="small" /></IconButton>
                                                            <IconButton size="small" onClick={() => handleDeleteSubCategory(sc.id)} color="error"><Delete fontSize="small" /></IconButton>
                                                        </Box>
                                                    </Box>
                                                </CardContent>
                                            </Card>
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : (
                                <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 3 }}>
                                    <Table size="small">
                                        <TableHead sx={{ bgcolor: 'grey.100' }}>
                                            <TableRow>
                                                <TableCell align="right">تصویر</TableCell>
                                                <TableCell align="right">عنوان</TableCell>
                                                <TableCell align="right">ترتیب</TableCell>
                                                <TableCell align="right">صفحه اصلی</TableCell>
                                                <TableCell align="center">عملیات</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {formData.subCategories.map((sc, index) => (
                                                <TableRow key={sc.id || index}>
                                                    <TableCell align="right">
                                                        <Avatar src={getImageUrl(sc.image)} variant="rounded" sx={{ width: 40, height: 40 }} />
                                                    </TableCell>
                                                    <TableCell align="right">{sc.title}</TableCell>
                                                    <TableCell align="right">{sc.order || 0}</TableCell>
                                                    <TableCell align="right">{sc.showOnHomePage ? 'بله' : 'خیر'}</TableCell>
                                                    <TableCell align="center">
                                                        <IconButton size="small" onClick={() => handleEditSubCategory(sc.id)} color="primary"><Edit fontSize="small" /></IconButton>
                                                        <IconButton size="small" onClick={() => handleDeleteSubCategory(sc.id)} color="error"><Delete fontSize="small" /></IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )
                        ) : (
                            <Box sx={{ textAlign: 'center', py: 3, border: '1px dashed', borderColor: 'divider', borderRadius: 3 }}>
                                <Typography variant="body2" color="text.secondary">هیچ زیر دسته‌ای تعریف نشده است</Typography>
                            </Box>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2, bgcolor: isMobile ? 'grey.50' : 'transparent' }}>
                    <Button onClick={handleClose} variant="outlined" sx={{ borderRadius: 2, px: 3 }}>انصراف</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary" disabled={saving} sx={{ borderRadius: 2, px: 3 }}>
                        {saving ? 'در حال ذخیره...' : (currentCategory ? 'بروزرسانی' : 'ذخیره')}
                    </Button>
                </DialogActions>
            </Dialog>


        </Container>
    );
};

export default WelfareCategories;


